import React, { useCallback, useEffect, useRef, useState } from "react";
import MainLayout from "../components/Layout/MainLayout";
import { Button, Dropdown, Space, Breadcrumb, message } from "antd";
import SearchInput from "../components/Forms/SearchInput";
import { FaCaretDown } from "react-icons/fa";
import {
  useNavigate,
  useSearchParams,
  Link,
  useOutletContext,
} from "react-router-dom";
import FileUploadModal from "../components/models/FileUploadModal";
import { postApi } from "../services/api";
import { items, roleIds } from "../constant";
import { FiEdit2 } from "react-icons/fi";
import DragDropTable from "../components/Tables/DragDropTable";
import { useGlobalModal } from "../context/SuccessModalProvider";
import { useSelector } from "react-redux";
import { RiDeleteBin6Line } from "react-icons/ri";
import Loader from "../common/Loader";
import ProductImageName from "../components/Layout/ProductImageName";
import { calcInStock, getStockStatus, handleRouteNav } from "../utils";
import CustomBadge from "../components/Charts/CustomBadge";
import { ProductMorePagesButtons } from "./Products";

const ProductBestSeller = () => {
  const { showPopup } = useGlobalModal();
  const { user_id, full_name, is_admin, is_sales_manager, role_id } =
    useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  const [productData, setProductData] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [loading, setLoading] = useState(false);
  const fetchIdRef = useRef(0);

  const fetchProducts = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      setLoading(true); // start loader
      const response = await postApi("sales/product/best-seller/list", {
        search: query,
      });

      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      const formattedRows = (response.data || []).map((item, idx) => ({
        ...item,
        id: item.id ?? item.product?.id ?? idx, // stable identifier
        key: item.id ?? item.product?.id ?? idx, // AntD convenience
        productImageUrl: item.product?.product_pic_url,
        orderIndex: item.sort_bestseller ?? idx,
      }));

      setProductData(formattedRows);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      console.error("Error fetching products:", error);
    } finally {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      setLoading(false); // stop loader
    }
  }, [query]);

  // ✅ call inside useEffect (with debounce)
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => clearTimeout(handler);
  }, [fetchProducts]);

  const columns = [
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (item, row) => (
        <ProductImageName
          img_url={item?.product_pic_url}
          img_alt={row?.short_name}
          text={
            <div>
              <div className="font-semibold">{row?.short_name}</div>
              <div className="text-xs text-gray-500">{row?.clienttype}</div>
            </div>
          }
        />
      ),
      ellipsis: true,
      width: 200,
    },
    // {
    //   title: "Price",
    //   dataIndex: "rate",
    //   key: "rate",
    //   render: (value) => `₹${value} / meter`,
    //   ellipsis: true,
    //   width: 100,
    // },
    {
      title: "Availability",
      dataIndex: "quality_code",
      key: "quality_code",
      render: (_, item) => {
        const { key, text, variant } = getStockStatus(item.quality_shade);

        return (
          <div className="w-full flex items-center px-2 py-2">
            <CustomBadge variant={variant} text={text} />
          </div>
        );
      },
      ellipsis: true,
      width: 140,
    },
    {
      title: "Width",
      dataIndex: "finish_width",
      key: "finish_width",
      render: (value) => `${Number(value)}''`,
      ellipsis: true,
      width: 80,
    },
  ];

  const handleRowClick = (record) => {
    navigate(handleRouteNav(`/product-details/${record.sort_no}`, role_id));
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    setTitle("Top Selling Products");
    setDescription(
      <div>
        <Breadcrumb>
          {[
            { title: "Home", href: handleRouteNav("/dashboard", role_id) },
            {
              title: "Product Catalogue ",
              href: handleRouteNav("/product-catalog", role_id),
            },
            {
              title: "Top Selling Products",
              href: handleRouteNav("/product-catalog/top-selling", role_id),
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href}>{item.title}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
    setBackButton(true);
  }, [setTitle, setDescription, setBackButton]);

  return (
    <>
      <>
        {/* Filter Buttons */}
        <ProductMorePagesButtons
          activeButton={"best-selling"}
          role_id={role_id}
        />
        <div
          className="bg-[#FFFFFF]"
          style={{ borderRadius: "32px", overflow: "hidden" }}
        >
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader size={60} />{" "}
            </div>
          ) : (
            <DragDropTable
              columns={columns}
              data={productData}
              handleRow={handleRowClick}
              defaultSortKey="sort_bestseller"
              onSave={async (payload) => {
                console.log("Payload for API:", payload);
                try {
                  if (payload.deletions?.length > 0) {
                    await Promise.all(
                      payload.deletions.map((item) =>
                        postApi("sales/product/add-is-bestseller", {
                          updated_by: full_name,
                          updated_by_id: user_id,
                          sort_no: item.sort_no,
                          quality_code: item.quality_code,
                          is_bestseller: 0,
                        }),
                      ),
                    );
                  }
                  if (payload.order?.length > 0) {
                    const response = await postApi(
                      "sales/product/rearrange-bestseller",
                      {
                        order: payload.order,
                      },
                    );

                    if (!response?.status) throw new Error(response?.message);

                    showPopup({
                      title: null,
                      content: (
                        <span className="custom-modal-title">
                          {response.message}
                        </span>
                      ),
                      onOk: () => console.log("Confirmed!"),
                      className: "custom-success-modal",
                    });
                  }
                  // refresh product list
                  fetchProducts();
                } catch (error) {
                  console.error(
                    "Upload failed:",
                    error.response?.data || error.message,
                  );
                  showPopup({
                    title: "Error",
                    content: (
                      <span className="custom-modal-title">
                        {error.response?.data?.message ||
                          "Something went wrong"}
                      </span>
                    ),
                    className: "custom-error-modal",
                  });
                }
              }}
              hideActions={!is_sales_manager && !is_admin}
            />
          )}
        </div>
      </>
    </>
  );
};

export default ProductBestSeller;
